'use client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAccount, useChainId } from 'wagmi'

import { paths } from '@/config/paths'
import ApplePay from '@/ui/assets/components/apple-pay.inline.svg'
import GooglePay from '@/ui/assets/components/google-pay.inline.svg'
import { GradientBadge } from '@/ui/atoms/badge/Badge'
import { Button, LinkButton } from '@/ui/atoms/button/Button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { Link } from '@/ui/atoms/link/Link'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'

import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Check,
  CreditCard,
  Eye,
  Info,
  Loader2,
  Pencil,
  Send,
  Wallet,
  X,
  Zap,
} from 'lucide-react'
import { AddCardWizard } from './AddCardWizard'
import { CardCarousel } from './CardCarousel'
import { CardConfiguration } from './CardConfiguration'
import { CardDetailsModal } from './CardDetailsModal'
import { CardWarningPanel } from './CardWarningPanel'
import { CardWithDisclaimer } from './CardWithDisclaimer'
import { DelegationApprovalBox } from './DelegationApprovalBox'
import { HookManagement } from './HookManagement'

import { SpendAllowanceStatus } from './SpendAllowanceStatus'
import { SpendLimitInline } from './SpendLimitInline'
import { SponsoredSpendBadge } from './SponsoredSpendBadge'
import { TransactionHistoryTab } from './tabs/TransactionHistoryTab'

import { useSelectedCard } from '../contexts/SelectedCardContext'
import { useATokenApproval } from '../hooks/useATokenApproval'
import { type RainFrequency, useBorrowLimit } from '../hooks/useBorrowLimit'
import { useCreateCard } from '../hooks/useCreateCard'
import { useDelegation } from '../hooks/useDelegation'
import { useEulerCardData } from '../hooks/useEulerCardData'
import { useEulerDelegation } from '../hooks/useEulerDelegation'
import { useEulerWithdrawDelegation } from '../hooks/useEulerWithdrawDelegation'
import { useHypurrFiCardData } from '../hooks/useHypurrFiCardData'
import { usePooledBorrowEModeMismatch } from '../hooks/usePooledBorrowEModeMismatch'
import { useSolverOptions } from '../hooks/useSolverOptions'
import { useSolvers } from '../hooks/useSolvers'
import { useSponsoredSpendStatus } from '../hooks/useSponsoredSpendStatus'
import { useTelegramNotificationStatus } from '../hooks/useTelegramNotificationStatus'
import { useTransactionHistory } from '../hooks/useTransactionHistory'
import { useUpdateCardLabel } from '../hooks/useUpdateCardLabel'
import { useUserCards } from '../hooks/useUserCards'

import { DEFAULT_APPROVAL_LIMIT } from '@/config/card'

import { FeatureFlag, useFeatureFlag } from '@/config/featureFlags'
import { useOpenDialog } from '@/domain/hooks/useDialog'
import { useCardSolverConfigs } from '@/domain/state'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { eModeDialogConfig } from '@/features/dialogs/e-mode/EModeDialog'
import { getTokenImage } from '@/ui/assets/tokens'
import Image from 'next/image'
import { Address } from 'viem'
import { CREDIT_TYPES, MAX_CARD_LIMIT, getActiveCardConfig, getHookTypeLabel } from '../constants'
import { useVaultClassifier } from '../contexts/VaultClassifierContext'
import { getSelectedPeriodMaxUSD } from '../utils/limitCaps'

type DashboardView = 'dashboard' | 'configure'
type SimulatedView = 'none' | 'loading' | 'creating' | 'error' | 'setting-up' | 'frozen'

interface CardDashboardProps {
  onAuthenticationFailed?: () => void
  maxCards?: number
}

export function CardDashboard({ onAuthenticationFailed, maxCards }: CardDashboardProps) {
  const isDev = process.env.NODE_ENV === 'development'
  const chainId = useChainId()
  const { address: userAddress } = useAccount()
  const openDialog = useOpenDialog()

  const { isLoading, isCreatingCard, error, refetch, resetCreateCard } = useUserCards()
  useSolvers()
  const {
    eulerBorrowVaults: _eulerBorrowVaults,
    primeEulerVaults: _primeEulerVaults,
    yieldEulerVaults: _yieldEulerVaults,
    hypurrFiBorrowTokens,
    hypurrFiDebtTokenByBorrowToken,
    hypurrFiATokenByBaseToken,
    isLoading: isSolverLoading,
  } = useSolverOptions()
  const { selectedCard: activeCard, selectedCardId, setSelectedCardId, cards } = useSelectedCard()
  const { syncWalletCardSolverConfigs } = useCardSolverConfigs()
  const { activeCampaign: sponsoredCampaign } = useSponsoredSpendStatus()
  const { active: telegramNotificationsActive, isLoading: isTelegramStatusLoading } = useTelegramNotificationStatus()
  const { resolveMarket } = useVaultClassifier()
  const hasActiveCard = !!activeCard
  const { data: borrowLimitData, updateLimit, isUpdating } = useBorrowLimit(activeCard?.cardID || '')

  useEffect(() => {
    if (!userAddress) return
    if (isLoading || error) return

    syncWalletCardSolverConfigs(
      userAddress,
      cards.map((card) => ({
        cardID: card.cardID,
        solverType: card.solverType,
        chainID: card.chainID,
        cardSolverConfig: card.solverConfig,
        status: card.status,
        label: card.label,
        last4: card.last4,
      })),
    )
  }, [cards, error, isLoading, syncWalletCardSolverConfigs, userAddress])

  // Use user's configured spend limit for delegation check, fallback to default
  const userSpendLimit = borrowLimitData?.limit ? Math.ceil(Number(borrowLimitData.limit)) : DEFAULT_APPROVAL_LIMIT

  const showHookManagement = useFeatureFlag(FeatureFlag.CARD_HOOK_MANAGEMENT)
  const [hookDialogMode, setHookDialogMode] = useState<'add' | 'edit' | 'remove' | null>(null)

  const [currentView, setCurrentView] = useState<DashboardView>('dashboard')
  const [isCardDetailsOpen, setIsCardDetailsOpen] = useState(false)
  const [isAddCardOpen, setIsAddCardOpen] = useState(false)
  const [paymentInstructionType, setPaymentInstructionType] = useState<'google' | 'apple' | null>(null)
  const [simulatedView, setSimulatedView] = useState<SimulatedView>('none')
  const { isPending: isCreatingNewCard } = useCreateCard()
  const { updateLabel } = useUpdateCardLabel()

  const handleAddCard = useCallback(() => {
    setIsAddCardOpen(true)
  }, [])

  const cardConfig = useMemo(
    () =>
      activeCard
        ? getActiveCardConfig(activeCard, resolveMarket)
        : getActiveCardConfig({ solverType: 'hypurrFi', solverConfig: {} }, resolveMarket),
    [activeCard, resolveMarket],
  )

  const borrowTokenAddress = (cardConfig.borrowTokenAddress ?? hypurrFiBorrowTokens[0]?.address ?? '') as Address
  const borrowVaultAddress = cardConfig.borrowVaultAddress as Address | undefined
  const pooledDebtTokenAddress =
    (hypurrFiDebtTokenByBorrowToken[borrowTokenAddress.toLowerCase()] as Address | undefined) || undefined
  const activeSpendATokenAddress = ((activeCard?.solverConfig?.aTokenAddress as string | undefined) ||
    hypurrFiATokenByBaseToken[borrowTokenAddress.toLowerCase()]) as Address | undefined
  const activeEulerSupplyVaultAddress = activeCard?.solverConfig?.supplyVaultAddress as string | undefined

  // Dashboard subscribes to read-only delegation/approval state (loading +
  // needsDelegation / needsApproval signals) to drive gates and the status
  // badge. The actual approval mutations live inside the inline
  // DelegationApprovalBox / SpendAllowanceStatus — the "Complete Setup" button
  // below forwards clicks to those boxes via `setupApproveRef`, so only a
  // single `useSendTransaction` is in flight across all triggers.
  const { needsDelegation, isLoadingAllowance, currentAllowance } = useDelegation({
    requiredLimit: userSpendLimit,
    debtTokenAddress: pooledDebtTokenAddress,
    borrowTokenAddress,
  })

  const { needsDelegation: eulerNeedsDelegation, isLoadingAuthorization: isLoadingEulerAuthorization } =
    useEulerDelegation({
      enabled: cardConfig.delegationType === 'euler',
      subAccountId: cardConfig.eulerSubAccountId,
      borrowVaultAddress,
    })

  const { needsDelegation: eulerWithdrawNeedsDelegation, isLoadingAuthorization: isLoadingEulerWithdrawAuthorization } =
    useEulerWithdrawDelegation({
      enabled: cardConfig.delegationType === 'euler-withdraw',
      subAccountId: cardConfig.eulerSubAccountId,
      supplyVaultAddress: cardConfig.supplyVaultAddress as `0x${string}` | undefined,
    })

  const {
    needsApproval: aTokenNeedsApproval,
    isAllowanceLoaded: isATokenAllowanceLoaded,
    isLoadingAllowance: isATokenAllowanceLoading,
  } = useATokenApproval({
    tokenAddress: activeSpendATokenAddress,
    requiredLimit: userSpendLimit,
    creditType: CREDIT_TYPES.HYPURRFI_SPEND,
  })

  // Ref to the approve button inside whichever onchainSetupBox is rendered.
  // `handleCompleteSetup` below forwards the primary-button click to this
  // element so that one mutation instance (owned by the box) handles the
  // approval regardless of which CTA the user pressed.
  const setupApproveRef = useRef<HTMLButtonElement>(null)

  // Kept near the other top-level hooks so it runs in a stable order across
  // renders (must never sit after an early-return). Consumed below by the
  // spend-allowance branch of handleCompleteSetup and by the approval boxes.
  const aTokenApprovalParams = useMemo(
    () => ({
      creditType: CREDIT_TYPES.HYPURRFI_SPEND,
      creditLimit: userSpendLimit,
      userParams: {
        aTokenAddress: activeSpendATokenAddress,
        purchaseBaseTokenAddress: borrowTokenAddress,
      },
    }),
    [userSpendLimit, activeSpendATokenAddress, borrowTokenAddress],
  )

  const hypurrfiData = useHypurrFiCardData({
    chainId,
    borrowTokenAddress,
    enabled: cardConfig.marketDataSource === 'hypurrFi',
  })

  const eulerData = useEulerCardData({
    chainId: cardConfig.chainId,
    borrowVaultAddress,
    userAddress,
    enabled: cardConfig.marketDataSource === 'euler',
    subAccountId: cardConfig.eulerSubAccountId,
  })

  const marketData =
    cardConfig.marketDataSource === 'euler'
      ? eulerData
      : cardConfig.marketDataSource === 'hypurrFi'
        ? hypurrfiData
        : null

  const totalBorrowUSD = marketData?.totalBorrowUSD
  const maxBorrowCapacityUSD = marketData?.maxBorrowCapacityUSD ?? 0
  const borrowedUSDForCapacity =
    cardConfig.marketDataSource === 'euler' ? eulerData?.totalDebtBorrowUSD ?? 0 : totalBorrowUSD?.toNumber() ?? 0
  const borrowedUSD =
    cardConfig.marketDataSource === 'euler' ? eulerData?.totalDebtValueUSD ?? 0 : totalBorrowUSD?.toNumber() ?? 0

  const availableSpendingPower = useMemo(() => {
    if (cardConfig.mode === 'spend') {
      const supplyVault = (activeCard?.solverConfig?.supplyVaultAddress as string | undefined)?.toLowerCase()
      const aToken = activeSpendATokenAddress?.toLowerCase()

      if (cardConfig.marketDataSource === 'euler' && supplyVault) {
        const activeSubAccountId = activeCard?.solverConfig?.subAccountId as number | undefined
        const pos = eulerData.spendModeCollateralPositions.find(
          (p) =>
            p.vaultAddress.toLowerCase() === supplyVault &&
            (activeSubAccountId == null || p.subAccountId === activeSubAccountId),
        )
        return pos ? pos.spendingPowerUSD.toNumber() : 0
      }

      if (cardConfig.marketDataSource === 'hypurrFi' && aToken) {
        const pos = hypurrfiData.spendModeCollateralPositions.find(
          (p) => p.reserve.aToken.address.toLowerCase() === aToken,
        )
        return pos ? ('spendingPowerUSD' in pos ? pos.spendingPowerUSD.toNumber() : 0) : 0
      }

      return 0
    }

    return Math.max(maxBorrowCapacityUSD - borrowedUSDForCapacity, 0)
  }, [
    cardConfig.mode,
    cardConfig.marketDataSource,
    activeCard?.solverConfig?.supplyVaultAddress,
    activeCard?.solverConfig?.subAccountId,
    activeSpendATokenAddress,
    eulerData.spendModeCollateralPositions,
    hypurrfiData.spendModeCollateralPositions,
    maxBorrowCapacityUSD,
    borrowedUSDForCapacity,
  ])

  const { message: pooledBorrowEModeMismatchMessage, userEModeCategoryId: pooledBorrowUserEModeCategoryId } =
    usePooledBorrowEModeMismatch({
      marketDataSource: cardConfig.marketDataSource,
      mode: cardConfig.mode,
      borrowTokenAddress,
      marketInfo: hypurrfiData.marketInfo,
    })

  const displayedSpendingPower = pooledBorrowEModeMismatchMessage ? 0 : availableSpendingPower

  // Detect the "savings-only" case for an Euler Borrow card. Matches the
  // dashboard's Sub-account Positions classification (see `SubAccountPositions`):
  // a deposit contributes to spending power only if it's enabled via EVC AND
  // its market matches the controller's market. Anything else counts as
  // savings-that-don't-contribute.
  const controllerMarketKeyForBorrow = useMemo(() => {
    if (cardConfig.marketDataSource !== 'euler') return undefined
    if (cardConfig.mode !== 'borrow') return undefined
    if (!borrowVaultAddress) return undefined
    return resolveMarket(borrowVaultAddress)?.key
  }, [cardConfig.marketDataSource, cardConfig.mode, borrowVaultAddress, resolveMarket])

  const eulerSavingsOnlyUSD = useMemo(() => {
    if (cardConfig.marketDataSource !== 'euler') return 0
    if (cardConfig.mode !== 'borrow') return 0
    const enabledSet = new Set((eulerData.enabledCollateralVaults ?? []).map((a) => a.toLowerCase()))
    let total = 0
    for (const pos of eulerData.collateralPositions) {
      const isEnabled = enabledSet.has(pos.vaultAddress.toLowerCase())
      if (!isEnabled) {
        total += Number(pos.depositAmountUSD)
        continue
      }
      if (!controllerMarketKeyForBorrow) continue
      const vaultMarketKey = resolveMarket(pos.vaultAddress as Address)?.key
      if (vaultMarketKey !== controllerMarketKeyForBorrow) {
        total += Number(pos.depositAmountUSD)
      }
    }
    return total
  }, [
    cardConfig.marketDataSource,
    cardConfig.mode,
    eulerData.enabledCollateralVaults,
    eulerData.collateralPositions,
    controllerMarketKeyForBorrow,
    resolveMarket,
  ])
  const showEulerSavingsOnlyHint =
    cardConfig.marketDataSource === 'euler' &&
    cardConfig.mode === 'borrow' &&
    displayedSpendingPower === 0 &&
    eulerSavingsOnlyUSD > 0 &&
    !pooledBorrowEModeMismatchMessage

  const { data: txData } = useTransactionHistory(activeCard?.cardID || '')

  const totalSpend = useMemo(() => {
    const allTx = txData?.pages.flatMap((page) => page.data?.transactions ?? []) ?? []
    return allTx.reduce((total, tx) => total + tx.amount / 100, 0)
  }, [txData])

  const periodSpend = useMemo(() => {
    const allTx = txData?.pages.flatMap((page) => page.data?.transactions ?? []) ?? []
    const freq = borrowLimitData?.frequency ?? 'per24HourPeriod'

    if (freq === 'perAuthorization') {
      if (allTx.length === 0) return 0
      const sorted = [...allTx].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      return sorted[0]!.amount / 100
    }

    if (freq === 'allTime') {
      return allTx.reduce((sum, tx) => sum + tx.amount / 100, 0)
    }

    const now = Date.now()
    const msPerDay = 86_400_000
    const periodMs: Record<string, number> = {
      per24HourPeriod: msPerDay,
      per7DayPeriod: 7 * msPerDay,
      per30DayPeriod: 30 * msPerDay,
      perYearPeriod: 365 * msPerDay,
    }
    const window = periodMs[freq]
    if (!window) return allTx.reduce((sum, tx) => sum + tx.amount / 100, 0)
    const cutoff = now - window
    return allTx
      .filter((tx) => new Date(tx.created_at).getTime() >= cutoff)
      .reduce((sum, tx) => sum + tx.amount / 100, 0)
  }, [txData, borrowLimitData?.frequency])

  useEffect(() => {
    if (error?.message?.includes('Authentication failed') && onAuthenticationFailed) {
      onAuthenticationFailed()
    }
  }, [error, onAuthenticationFailed])

  const currentMode = cardConfig.mode === 'spend' ? 'Spend' : 'Borrow'
  const borrowTokenSymbol = marketData?.borrowTokenSymbol
  const isEulerPosition = cardConfig.protocol !== 'Pooled'

  const eulerSpendPositions = cardConfig.mode === 'spend' ? eulerData.spendModeCollateralPositions : undefined
  const hypurrfiSpendPositions = cardConfig.mode === 'spend' ? hypurrfiData.spendModeCollateralPositions : undefined

  const spendTokenSymbol = useMemo(() => {
    if (eulerSpendPositions && activeEulerSupplyVaultAddress) {
      const activeSubAccountId = activeCard?.solverConfig?.subAccountId as number | undefined
      const pos = eulerSpendPositions.find(
        (p) =>
          p.vaultAddress.toLowerCase() === activeEulerSupplyVaultAddress.toLowerCase() &&
          (activeSubAccountId == null || p.subAccountId === activeSubAccountId),
      )
      if (pos) return pos.token.symbol
    }
    if (hypurrfiSpendPositions && activeSpendATokenAddress) {
      const pos = hypurrfiSpendPositions.find(
        (p) => p.reserve.aToken.address.toLowerCase() === activeSpendATokenAddress.toLowerCase(),
      )
      if (pos) return pos.reserve.token.symbol
    }
    return undefined
  }, [
    activeEulerSupplyVaultAddress,
    activeSpendATokenAddress,
    eulerSpendPositions,
    hypurrfiSpendPositions,
    activeCard?.solverConfig?.subAccountId,
  ])

  const displayTokenSymbol = cardConfig.mode === 'spend' ? spendTokenSymbol : borrowTokenSymbol
  const marketDisplayName = `HypurrFi ${cardConfig.protocol} ${displayTokenSymbol ?? ''}`
  const positionSubLabel = isEulerPosition ? `Position ${cardConfig.eulerSubAccountId ?? 0}` : 'Pooled'

  const handleUpdateLimit = async (newLimit: number, frequency?: RainFrequency) => {
    const selectedFrequency = frequency ?? borrowLimitData?.frequency ?? 'per24HourPeriod'
    const selectedMax = getSelectedPeriodMaxUSD({
      tier: borrowLimitData?.tier,
      frequency: selectedFrequency,
      globalMaxUSD: MAX_CARD_LIMIT,
    })
    const clamped = Math.min(Math.max(newLimit, 1), selectedMax)
    try {
      await updateLimit({
        limit: Math.round(clamped * 100),
        frequency: selectedFrequency,
        cardID: activeCard?.cardID || '',
      })
    } catch (err) {
      console.error('Error updating spend limit:', err)
    }
  }

  const isSimulating = isDev && simulatedView !== 'none'

  const simLoading = isSimulating && (simulatedView === 'loading' || simulatedView === 'creating')
  const simError = isSimulating && simulatedView === 'error'
  const simSettingUp = isSimulating && simulatedView === 'setting-up'
  const isSpendAllowanceLoading = cardConfig.delegationType === 'spend-allowance' && isATokenAllowanceLoading
  const isEulerDelegationLoading = cardConfig.delegationType === 'euler' && isLoadingEulerAuthorization
  const isEulerWithdrawDelegationLoading =
    cardConfig.delegationType === 'euler-withdraw' && isLoadingEulerWithdrawAuthorization

  const devToolbar = isDev ? (
    <div className="fixed right-4 bottom-4 z-50 flex items-center gap-1 rounded-lg border border-border bg-background/95 p-1.5 shadow-lg backdrop-blur-sm">
      <Typography variant="p" className="px-2 text-muted-foreground text-xs">
        Dev
      </Typography>
      {(['none', 'loading', 'creating', 'error', 'setting-up', 'frozen'] as SimulatedView[]).map((view) => (
        <button
          key={view}
          onClick={() => setSimulatedView(view)}
          className={cn(
            'rounded px-2 py-1 text-xs transition-colors',
            simulatedView === view ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {view === 'none' ? 'Live' : view}
        </button>
      ))}
    </div>
  ) : null

  if (currentView === 'configure' && !isSimulating) {
    return (
      <>
        <CardConfiguration key={selectedCardId} onBack={() => setCurrentView('dashboard')} />
        {devToolbar}
      </>
    )
  }

  if (
    simLoading ||
    (!isSimulating &&
      (isLoading ||
        isCreatingCard ||
        isLoadingAllowance ||
        isSpendAllowanceLoading ||
        isEulerDelegationLoading ||
        isEulerWithdrawDelegationLoading ||
        isSolverLoading))
  ) {
    return (
      <>
        <div className="flex flex-col items-center justify-center gap-6 py-20">
          <div className="relative">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
            <CreditCard className="absolute inset-0 m-auto h-6 w-6 text-primary" />
          </div>
          <Typography variant="p" className="text-muted-foreground">
            {(isSimulating && simulatedView === 'creating') || isCreatingCard
              ? 'Creating your card...'
              : 'Loading your card...'}
          </Typography>
        </div>
        {devToolbar}
      </>
    )
  }

  if (simError || (!isSimulating && !hasActiveCard && error)) {
    return (
      <>
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
          <div className="flex flex-1 flex-col gap-6">
            <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-red-500/10 via-transparent to-red-400/5" />
              <Panel.Content className="relative flex flex-col gap-6 p-8">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-red-500/30 bg-red-500/10">
                    <AlertCircle className="h-6 w-6 text-red-400" />
                  </div>
                  <Typography variant="h3" className="text-red-400">
                    Card Creation Failed
                  </Typography>
                </div>

                <Typography variant="p" className="text-muted-foreground">
                  {isSimulating
                    ? 'Simulated error: Something went wrong during card creation.'
                    : error?.message ?? 'An unexpected error occurred while creating your card.'}
                </Typography>

                <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                  <Typography variant="p" className="text-red-400 text-sm">
                    This may be a temporary issue. Try again or contact support if the problem persists.
                  </Typography>
                </div>

                <Button
                  onClick={() => {
                    if (isSimulating) {
                      setSimulatedView('none')
                    } else {
                      resetCreateCard()
                      refetch()
                    }
                  }}
                  size="lg"
                  className="w-full transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                  postfixIcon={<ArrowRight className="h-4 w-4" />}
                >
                  Try Again
                </Button>
              </Panel.Content>
            </Panel.Wrapper>
          </div>

          <div className="relative flex items-center justify-center lg:flex-1">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-red-500/10 to-red-400/10 blur-[60px]" />
            </div>
            <div className="relative opacity-60 grayscale transition-all duration-500 hover:opacity-80 hover:grayscale-0">
              <CardWithDisclaimer className="h-auto w-full max-w-sm drop-shadow-[0_0_40px_rgba(239,68,68,0.15)]" />
            </div>
          </div>
        </div>
        {devToolbar}
      </>
    )
  }

  if (simSettingUp || (!isSimulating && !hasActiveCard)) {
    return (
      <>
        <div className="flex flex-col items-center justify-center gap-6 py-20">
          <div className="relative">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
            <CreditCard className="absolute inset-0 m-auto h-6 w-6 text-primary" />
          </div>
        </div>
        {devToolbar}
      </>
    )
  }

  const cardStatus = activeCard?.status?.toLowerCase()
  const isFrozen = cardStatus === 'locked' || cardStatus === 'frozen'
  const isSuspended = cardStatus === 'suspended' || cardStatus === 'inactive'

  const needsOnchainSetup =
    (cardConfig.delegationType === 'pooled' && needsDelegation && currentAllowance === 0) ||
    (cardConfig.delegationType === 'euler' && eulerNeedsDelegation) ||
    (cardConfig.delegationType === 'euler-withdraw' && eulerWithdrawNeedsDelegation) ||
    (cardConfig.delegationType === 'spend-allowance' && aTokenNeedsApproval && isATokenAllowanceLoaded)

  // Primary "Complete Setup" button forwards its click to the approve button
  // inside whichever inline onchainSetupBox is rendered. That keeps a single
  // `useSendTransaction` / mutation in flight — clicks on a disabled button
  // are no-ops in the DOM, so rapid double-clicks or fan-out across multiple
  // CTAs can't trigger a second approval transaction.
  const handleCompleteSetup = () => {
    if (!needsOnchainSetup) return
    setupApproveRef.current?.click()
  }

  const { statusLabel, statusStyles, statusHint } = (() => {
    if (isSuspended) {
      return {
        statusLabel: 'Suspended',
        statusStyles: 'bg-red-500/20 text-red-400 border-red-500/30',
        statusHint: 'Contact support',
      }
    }
    if (isFrozen) {
      return {
        statusLabel: 'Frozen',
        statusStyles: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        statusHint: undefined,
      }
    }
    if (needsOnchainSetup) {
      const hint =
        cardConfig.delegationType === 'euler' || cardConfig.delegationType === 'euler-withdraw'
          ? 'Position setup required'
          : 'Borrow allowance not set'
      return {
        statusLabel: 'Setup Required',
        statusStyles: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
        statusHint: hint,
      }
    }
    if (cardConfig.delegationType === 'pooled' && needsDelegation) {
      return {
        statusLabel: 'Active',
        statusStyles: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        statusHint: 'Allowance low',
      }
    }
    return {
      statusLabel: 'Active',
      statusStyles: 'bg-green-500/20 text-green-400 border-green-500/30',
      statusHint: undefined,
    }
  })()

  const onchainSetupBox =
    cardConfig.delegationType === 'pooled' ? (
      <DelegationApprovalBox
        protocol="pooled"
        requiredLimit={userSpendLimit}
        debtTokenAddress={pooledDebtTokenAddress}
        borrowTokenAddress={borrowTokenAddress}
        approveButtonRef={setupApproveRef}
      />
    ) : cardConfig.delegationType === 'euler' ? (
      <DelegationApprovalBox
        protocol="euler"
        eulerSubAccountId={cardConfig.eulerSubAccountId}
        borrowVaultAddress={borrowVaultAddress}
        approveButtonRef={setupApproveRef}
      />
    ) : cardConfig.delegationType === 'euler-withdraw' ? (
      <DelegationApprovalBox
        protocol="euler-withdraw"
        eulerSubAccountId={cardConfig.eulerSubAccountId}
        supplyVaultAddress={cardConfig.supplyVaultAddress as `0x${string}` | undefined}
        approveButtonRef={setupApproveRef}
      />
    ) : null
  // Note: for `spend-allowance`, the setup/status UI is handled by
  // `SpendAllowanceStatus` in the bottom section below — it already covers
  // both the "Set to X" initial approval and the ongoing status display, so we
  // don't also render an `ATokenApprovalBox` here (would produce duplicate
  // approve buttons with independent `useSendTransaction` instances).

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center gap-3">
        <Typography variant="h2" className="text-3xl lg:text-4xl" gradient>
          HypurrFi Card
        </Typography>
        <GradientBadge className="w-fit" innerClassName="flex items-center gap-1.5 px-2 py-0.5">
          <CreditCard className="h-3 w-3 text-gradient-yellow" />
          <Typography variant="prompt" gradient>
            Beta
          </Typography>
        </GradientBadge>
      </div>
      <Typography variant="p" className="max-w-2xl text-muted-foreground">
        Use your card anywhere Visa is accepted. Your assets stay on-chain while you access instant purchasing power.
      </Typography>

      {cardConfig.configError && (
        <Panel.Wrapper className="overflow-hidden border-amber-500/30">
          <Panel.Content className="bg-amber-500/10 p-4">
            <Typography variant="p" className="text-amber-300 text-sm">
              {cardConfig.configError}. Use Change Solver to repair this card configuration.
            </Typography>
          </Panel.Content>
        </Panel.Wrapper>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel.Wrapper className="relative flex h-full flex-col overflow-hidden">
          <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-gradient-yellow/10 via-transparent to-gradient-green/10" />
          <Panel.Content className="relative flex flex-1 flex-col justify-between gap-3 p-4">
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className={cn('rounded-full border px-2.5 py-0.5 font-medium text-xs', statusStyles)}>
                    {statusLabel}
                  </span>
                  {statusHint && <span className="text-muted-foreground text-xs">{statusHint}</span>}
                </div>
                {activeCard && (
                  <InlineCardLabel
                    cardId={activeCard.cardID}
                    label={activeCard.label}
                    last4={activeCard.last4}
                    onSave={(cardId, label) => updateLabel({ cardID: cardId, label })}
                  />
                )}
              </div>

              {needsOnchainSetup && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
                  <Typography variant="p" className="text-amber-300 text-sm">
                    On-chain authorization required before this card can spend.
                  </Typography>
                </div>
              )}

              <CardCarousel
                cards={cards}
                selectedCardId={selectedCardId}
                onSelectCard={setSelectedCardId}
                onAddCard={handleAddCard}
                onUpdateLabel={(cardId, label) => updateLabel({ cardID: cardId, label })}
                isCreatingNewCard={isCreatingNewCard}
                showLabel={false}
                maxCards={maxCards}
                activeCardNeedsSetup={needsOnchainSetup}
              />
            </div>

            <div className="flex w-full flex-col gap-2">
              <Button
                onClick={needsOnchainSetup ? handleCompleteSetup : () => setIsCardDetailsOpen(true)}
                className="h-10 w-full bg-gradient-to-r from-gradient-yellow to-gradient-green text-black hover:opacity-90"
                prefixIcon={needsOnchainSetup ? <ArrowRight className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              >
                {needsOnchainSetup ? 'Complete Setup' : 'View Details'}
              </Button>
              <div className="flex items-center justify-center gap-3 sm:grid sm:grid-cols-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex w-14 sm:w-full">
                      <Button
                        variant="outline"
                        onClick={() => setPaymentInstructionType('apple')}
                        disabled={needsOnchainSetup}
                        className="flex h-10 w-14 items-center justify-center gap-2 px-0 sm:w-full sm:px-4"
                        aria-label="Apple Pay instructions"
                      >
                        <ApplePay className="h-6 w-auto text-foreground" />
                        <span className="hidden text-sm sm:inline">Apple Pay</span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {needsOnchainSetup && <TooltipContentShort>Finish setup to view card details</TooltipContentShort>}
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex w-14 sm:w-full">
                      <Button
                        variant="outline"
                        onClick={() => setPaymentInstructionType('google')}
                        disabled={needsOnchainSetup}
                        className="flex h-10 w-14 items-center justify-center gap-2 px-0 sm:w-full sm:px-4"
                        aria-label="Google Pay instructions"
                      >
                        <GooglePay className="h-6 w-auto" />
                        <span className="hidden text-sm sm:inline">Google Pay</span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {needsOnchainSetup && <TooltipContentShort>Finish setup to view card details</TooltipContentShort>}
                </Tooltip>
              </div>
            </div>
          </Panel.Content>
        </Panel.Wrapper>

        <Panel.Wrapper className="relative h-full overflow-hidden">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent" />
          <Panel.Content className="relative flex h-full flex-col divide-y divide-border p-0">
            <div className="flex flex-col gap-2 px-4 py-3.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                  <Typography variant="p" className="text-muted-foreground text-sm">
                    Spending Power
                  </Typography>
                  {cardConfig.marketDataSource === 'euler' && cardConfig.mode === 'borrow' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="flex h-4 w-4 items-center justify-center text-muted-foreground/70 hover:text-muted-foreground"
                          aria-label="How spending power is calculated"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContentShort>
                        Only deposits enabled as collateral on this Euler position count toward spending power. Savings
                        (deposits not enabled as collateral) are excluded - enable them in Manage to unlock borrow
                        capacity.
                      </TooltipContentShort>
                    </Tooltip>
                  )}
                </div>
                {!isTelegramStatusLoading &&
                  (telegramNotificationsActive ? (
                    <Link
                      href={paths.cardTelegramNotifications}
                      className="flex h-7 items-center gap-1.5 text-muted-foreground text-xs underline-offset-4 hover:text-foreground hover:underline"
                      aria-label="Telegram notifications enabled"
                    >
                      <Check className="h-3.5 w-3.5 text-green-500" />
                      Notifications enabled
                    </Link>
                  ) : (
                    <LinkButton
                      href={paths.cardTelegramNotifications}
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 border-telegram/40 bg-telegram/5 px-2.5 text-telegram text-xs hover:border-telegram hover:bg-telegram/10 hover:text-telegram"
                      aria-label="Setup notifications"
                    >
                      <Send className="h-3.5 w-3.5" />
                      <span>Setup notifications</span>
                    </LinkButton>
                  ))}
              </div>
              <div className="flex items-end justify-between">
                <Typography
                  variant="h2"
                  className={cn(
                    'font-bold leading-tight',
                    displayedSpendingPower > 0
                      ? 'bg-gradient-to-r from-gradient-yellow to-gradient-green bg-clip-text text-transparent'
                      : 'text-muted-foreground',
                  )}
                >
                  ${displayedSpendingPower.toFixed(2)}
                </Typography>

                <div className="flex items-center gap-4 pb-0.5">
                  {cardConfig.mode !== 'spend' && borrowTokenSymbol && (
                    <div className="space-y-0.5 text-right">
                      <Typography variant="p" className="text-muted-foreground text-xs">
                        Borrowed
                      </Typography>
                      <Typography variant="p" className="font-semibold">
                        ${borrowedUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </div>
                  )}
                  <div className="space-y-0.5 text-right">
                    <Typography variant="p" className="text-muted-foreground text-xs">
                      Total Spent
                    </Typography>
                    <Typography variant="p" className="font-semibold">
                      ${totalSpend.toFixed(2)}
                    </Typography>
                  </div>
                </div>
              </div>
              {pooledBorrowEModeMismatchMessage && (
                <CardWarningPanel
                  className="mt-2"
                  textClassName="text-xs"
                  message="This card is still configured for pooled borrow, but spending power is temporarily reduced to $0 because your current eMode does not match the borrow asset. Disable eMode or switch to a compatible borrow asset in Manage to restore spending power."
                  inlineActionLabel="Disable eMode"
                  onInlineActionClick={() =>
                    openDialog(eModeDialogConfig, {
                      userEModeCategoryId: pooledBorrowUserEModeCategoryId,
                    })
                  }
                />
              )}
              {showEulerSavingsOnlyHint && (
                <CardWarningPanel
                  className="mt-2"
                  textClassName="text-xs"
                  message={`You've deposited $${eulerSavingsOnlyUSD.toFixed(2)} in this position as savings, but none of it is enabled as collateral yet. Enable a vault in Manage to unlock spending power.`}
                  inlineActionLabel="Manage"
                  onInlineActionClick={() => setCurrentView('configure')}
                />
              )}
            </div>

            {sponsoredCampaign && (
              <div className="px-4 pt-3">
                <SponsoredSpendBadge campaign={sponsoredCampaign} variant="card" />
              </div>
            )}

            <div className="p-4">
              <SpendLimitInline
                spendLimit={borrowLimitData?.limit ? Number(borrowLimitData.limit) : undefined}
                frequency={borrowLimitData?.frequency ?? 'per24HourPeriod'}
                totalSpent={periodSpend}
                maxCapacity={maxBorrowCapacityUSD}
                tier={borrowLimitData?.tier}
                onUpdateLimit={handleUpdateLimit}
                isUpdating={isUpdating}
              />

              {/* ── Market Display ── */}
              <div className="mt-3 border-border border-t pt-3">
                <div className="flex items-center justify-between">
                  <div className="flex min-w-0 items-center gap-2">
                    {displayTokenSymbol && (
                      <Image
                        src={getTokenImage(TokenSymbol(displayTokenSymbol))}
                        alt={displayTokenSymbol}
                        width={24}
                        height={24}
                        className="rounded-full"
                      />
                    )}
                    <div className="flex flex-col">
                      <Typography
                        variant="p"
                        className="whitespace-nowrap bg-gradient-to-r from-gradient-yellow to-gradient-green bg-clip-text font-medium text-sm text-transparent leading-tight"
                      >
                        {marketDisplayName}
                      </Typography>
                      <span className="text-muted-foreground text-xs leading-tight">
                        {currentMode} · {positionSubLabel}
                      </span>
                    </div>
                    {marketData?.borrowAPY != null && cardConfig.mode !== 'spend' && (
                      <span className="rounded-md border border-border/50 bg-muted/40 px-1.5 py-0.5 text-muted-foreground text-xs">
                        {marketData.borrowAPY.toFixed(2)}% APY
                      </span>
                    )}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/*
                        A disabled <button> doesn't fire pointer events, so we
                        wrap it in a span so the tooltip trigger still picks up
                        hover/focus when the card is frozen.
                      */}
                      <span className="inline-flex">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCurrentView('configure')}
                          disabled={isFrozen}
                          className="text-muted-foreground text-xs"
                        >
                          Manage
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {isFrozen && <TooltipContentShort>Unfreeze card to manage position</TooltipContentShort>}
                  </Tooltip>
                </div>
              </div>

              {cardConfig.delegationType !== 'none' && (
                <div className="mt-3 border-border border-t pt-3">
                  {onchainSetupBox}
                  {cardConfig.delegationType === 'spend-allowance' && (
                    <SpendAllowanceStatus
                      approvalParams={aTokenApprovalParams}
                      tokenAddress={activeSpendATokenAddress}
                      approveButtonRef={setupApproveRef}
                    />
                  )}
                </div>
              )}

              {showHookManagement && activeCard && (
                <div className="mt-3 border-border border-t pt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-primary" />
                      <Typography variant="p" className="font-medium text-sm">
                        Post-Settlement Hook
                      </Typography>
                    </div>
                    {activeCard.hookType ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setHookDialogMode('edit')}
                          disabled={isFrozen}
                          className="text-muted-foreground text-xs"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setHookDialogMode('remove')}
                          disabled={isFrozen}
                          className="text-red-400 text-xs hover:text-red-300"
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setHookDialogMode('add')}
                        disabled={isFrozen}
                        className="text-muted-foreground text-xs"
                      >
                        Add Hook
                      </Button>
                    )}
                  </div>
                  {activeCard.hookType && (
                    <Typography variant="p" className="mt-1 text-muted-foreground text-xs">
                      {getHookTypeLabel(activeCard.hookType)} active
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      <TransactionHistoryTab />

      <CardDetailsModal isOpen={isCardDetailsOpen} onClose={() => setIsCardDetailsOpen(false)} card={activeCard} />

      {showHookManagement && hookDialogMode && activeCard && (
        <HookManagement
          open={!!hookDialogMode}
          onOpenChange={(open) => {
            if (!open) setHookDialogMode(null)
          }}
          card={activeCard}
          mode={hookDialogMode}
        />
      )}

      <AddCardWizard
        open={isAddCardOpen}
        onOpenChange={setIsAddCardOpen}
        currentCardCount={cards.length}
        maxCards={maxCards}
        onCardCreated={({ needsFunding, cardId }) => {
          if (cardId) {
            setSelectedCardId(cardId)
          }
          if (needsFunding) {
            setCurrentView('configure')
          }
        }}
      />

      <Dialog open={paymentInstructionType !== null} onOpenChange={() => setPaymentInstructionType(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle gradient>
              {paymentInstructionType === 'google' ? 'Add to Google Pay' : 'Add to Apple Pay'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4">
            {paymentInstructionType === 'google' ? (
              <GooglePay className="h-16 w-16" />
            ) : (
              <ApplePay className="h-16 w-16 text-foreground" />
            )}
          </div>
          <ol className="space-y-2">
            {(paymentInstructionType === 'google'
              ? [
                  'Open the Google Wallet app',
                  'Tap your profile picture and select the option to add a payment card',
                  'Tap "Add new card"',
                  'Enter your HypurrFi card details',
                  'Complete verification',
                ]
              : [
                  'Open the Wallet app on your iPhone',
                  'Tap the "+" button to add a new card',
                  'Choose "Enter Card Details Manually"',
                  'Enter your HypurrFi card details',
                  'Complete verification',
                ]
            ).map((step, i) => (
              <li key={i} className="flex gap-3 text-muted-foreground text-sm">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 font-medium text-primary text-xs">
                  {i + 1}
                </span>
                <span className="pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
          <Button onClick={() => setPaymentInstructionType(null)} variant="outline" className="mt-4 w-full">
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function InlineCardLabel({
  cardId,
  label,
  last4,
  onSave,
}: {
  cardId: string
  label: string | null
  last4: string
  onSave: (cardId: string, label: string) => Promise<void>
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [draft, setDraft] = useState(label ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setDraft(label ?? '')
  }, [label])

  const save = async () => {
    const trimmed = draft.trim()
    if (trimmed === (label ?? '')) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await onSave(cardId, trimmed)
      setIsEditing(false)
    } catch {
      setDraft(label ?? '')
    } finally {
      setIsSaving(false)
    }
  }

  const cancel = () => {
    setDraft(label ?? '')
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 50))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save()
            if (e.key === 'Escape') cancel()
          }}
          disabled={isSaving}
          placeholder={`Card ••${last4}`}
          className="w-36 rounded border border-primary/50 bg-transparent px-2 py-0.5 text-right text-foreground text-sm focus:outline-none"
          maxLength={50}
        />
        <button onClick={save} disabled={isSaving} className="rounded p-0.5 text-green-400 hover:bg-green-400/10">
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </button>
        <button onClick={cancel} className="rounded p-0.5 text-muted-foreground hover:bg-muted">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  return (
    <button
      onClick={() => {
        setIsEditing(true)
        requestAnimationFrame(() => {
          inputRef.current?.focus()
          inputRef.current?.select()
        })
      }}
      className="group flex items-center gap-1.5 text-muted-foreground text-sm hover:text-foreground"
      title="Rename card"
    >
      <span className="whitespace-nowrap">{label || 'Card'}</span>
      <span className="whitespace-nowrap text-muted-foreground/60 text-xs leading-none">••{last4}</span>
      <Pencil className="h-3 w-3 shrink-0 text-muted-foreground/60 group-hover:text-muted-foreground" />
    </button>
  )
}
